import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * "N contacts at <org>" (2026-09-28, Jack). Several people at one prospect
 * are several leads; this is how you see and jump between them. A button,
 * not a pill, so it reads as something you can open; the menu lists every
 * contact (the current one marked), scrolls past ~8, and so holds at 10 or
 * 50 contacts without growing the row or the header.
 */
export default function OrgContactsMenu({
  orgName,
  current,
  others,
  size = "md",
}: {
  orgName: string;
  /** The lead being viewed (record) - omitted on list rows. */
  current?: { id: string; contactName: string | null } | null;
  others: Array<{ id: string; name: string; contactName: string | null }>;
  size?: "sm" | "md";
}) {
  const navigate = useNavigate();
  const location = useLocation();
  // Keep the tab you are on (e.g. Proposals) when jumping to a colleague.
  const tab = new URLSearchParams(location.search).get("tab");
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  if (!others.length) return null;
  const total = others.length + (current ? 1 : 0);
  const label = current ? `${total} contacts at ${orgName}` : `+${others.length} more at this org`;
  const sm = size === "sm";

  return (
    <span ref={ref} style={{ position: "relative", display: "inline-block", verticalAlign: "middle" }} onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: sm ? "2px 8px" : "5px 12px",
          background: "#E6F1FB",
          color: "#185FA5",
          border: "1px solid #B9CFEA",
          borderRadius: 999,
          fontFamily: "Poppins",
          fontSize: sm ? 11 : 13,
          fontWeight: 600,
          cursor: "pointer",
          whiteSpace: "nowrap",
        }}
      >
        {label}
        <span style={{ fontSize: sm ? 9 : 10 }}>{open ? "\u25B2" : "\u25BC"}</span>
      </button>
      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            zIndex: 50,
            minWidth: 260,
            maxHeight: 320,
            overflowY: "auto",
            background: "#fff",
            border: "1px solid #E3E8EF",
            borderRadius: 8,
            boxShadow: "0 8px 24px rgba(0,0,0,.12)",
            fontFamily: "Poppins",
            fontSize: 13,
          }}
        >
          <div style={{ padding: "8px 12px", fontSize: 11, color: "#818181", textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 600 }}>
            Contacts at {orgName}
          </div>
          {current && (
            <div style={{ padding: "8px 12px", borderTop: "1px solid #F1F1F1", background: "#FAFAF8" }}>
              <b>{current.contactName || "No contact name"}</b>
              <span style={{ color: "#818181", fontSize: 11 }}> {"\u00b7"} this lead</span>
            </div>
          )}
          {others.map((o) => (
            <div
              key={o.id}
              onClick={() => { setOpen(false); navigate(`/leads/${o.id}${current && tab ? `?tab=${encodeURIComponent(tab)}` : ""}`); }}
              style={{ padding: "8px 12px", borderTop: "1px solid #F1F1F1", cursor: "pointer", color: "#1a2332" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#F5F8FC")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              {o.contactName || "No contact name"}
            </div>
          ))}
        </div>
      )}
    </span>
  );
}
